/**
 * Coalesce Application decline_reason + DE declinedReason/Explanation into one
 * ops-facing story. Vendor payloads often repeat the same RFSC01 prose across
 * all three fields; joining them as-is triples the message in the UI.
 *
 * Provenir / CA payloads may also echo approval labels (e.g. "CA-Approved") into
 * Op_declined_* or applications.decline_reason even when decision is APPROVE —
 * those must not drive the decline banner or Overview alerts.
 */

const PROVENIR_CODE_RE = /^\s*([A-Za-z]{1,8}\d{1,4})\s*(?::\s*|$)/;

export type DecisionDeclineCopy = {
  /** Short vendor code when present (e.g. RFSC01). */
  code: string | null;
  /** Single customer/ops explanation — never a concatenated duplicate. */
  explanation: string | null;
};

function normalizeWs(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function stripCodePrefix(raw: string): { code: string | null; prose: string } {
  const text = normalizeWs(raw);
  if (!text) return { code: null, prose: '' };
  const match = PROVENIR_CODE_RE.exec(text);
  if (!match) return { code: null, prose: text };
  const code = match[1]!.toUpperCase();
  const colon = text.indexOf(':');
  const prose = colon >= 0 ? normalizeWs(text.slice(colon + 1)) : '';
  return { code, prose };
}

/** Prefer longer unique strings; drop substrings / exact duplicates. */
function uniqueProse(parts: string[]): string | null {
  const cleaned = parts.map(normalizeWs).filter(Boolean);
  if (cleaned.length === 0) return null;

  const sorted = [...cleaned].sort((a, b) => b.length - a.length);
  const kept: string[] = [];
  for (const candidate of sorted) {
    const lower = candidate.toLowerCase();
    if (kept.some((k) => k.toLowerCase().includes(lower))) continue;
    kept.push(candidate);
  }
  // Stable: longest first already; join only if truly distinct leftovers.
  return kept.length === 1 ? kept[0]! : kept.join(' — ');
}

/**
 * Approval / capacity-status labels that vendors sometimes park in decline fields.
 * Not a hard-stop reason.
 */
export function looksLikeApprovalStatusLabel(text?: string | null): boolean {
  if (!text?.trim()) return false;
  const u = text
    .trim()
    .toUpperCase()
    .replace(/[_ ]+/g, '-')
    .replace(/-+/g, '-');
  if (u === 'APPROVED' || u === 'APPROVE' || u === 'CA-APPROVED' || u === 'CA-APPROVE') {
    return true;
  }
  // "CA: CA-Approved", "Status: Approved", bare "CA-Approved …"
  if (/^(CA:?-?)?APPROV/.test(u)) return true;
  if (/^CA:?-?APPROV/.test(u)) return true;
  return false;
}

export function isDecisionEngineDeclineDecision(decision?: string | null): boolean {
  const u = String(decision ?? '')
    .trim()
    .toUpperCase();
  return u === 'DECLINE' || u === 'ERROR' || u === 'FAIL';
}

/**
 * Whether ops UI should treat this as a DE hard decline.
 * Explicit APPROVE / REFER wins over echoed decline-field noise.
 */
export function isDecisionEngineHardDecline(input: {
  decision?: string | null;
  applicationDeclineReason?: string | null;
  applicationStatus?: string | null;
}): boolean {
  const decision = String(input.decision ?? '')
    .trim()
    .toUpperCase();
  if (isDecisionEngineDeclineDecision(decision)) return true;
  if (/^APPROV/.test(decision) || decision === 'REFER') return false;

  const appReason = input.applicationDeclineReason?.trim() ?? '';
  if (!appReason || looksLikeApprovalStatusLabel(appReason)) return false;

  const status = String(input.applicationStatus ?? '')
    .trim()
    .toUpperCase();
  // Without a crisp DE decision, only trust application.decline_reason when
  // the journey itself was hard-stopped.
  return status === 'DECLINED';
}

export function coalesceDecisionDeclineCopy(input: {
  applicationDeclineReason?: string | null;
  declinedReason?: string | null;
  declinedExplanation?: string | null;
}): DecisionDeclineCopy {
  const chunks = [
    input.applicationDeclineReason,
    input.declinedReason,
    input.declinedExplanation,
  ]
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter(Boolean)
    // Drop approval echoes (e.g. CA-Approved) so they never become "Decline reason".
    .filter((s) => !looksLikeApprovalStatusLabel(s));

  if (chunks.length === 0) return { code: null, explanation: null };

  let code: string | null = null;
  const proseParts: string[] = [];

  for (const chunk of chunks) {
    const parsed = stripCodePrefix(chunk);
    if (parsed.code && !code) code = parsed.code;
    if (parsed.prose) {
      if (!looksLikeApprovalStatusLabel(parsed.prose)) proseParts.push(parsed.prose);
    } else if (!parsed.code) {
      proseParts.push(normalizeWs(chunk));
    }
  }

  // Bare code-only chunks (e.g. declinedReason=RFSC01) contribute code only.
  return {
    code,
    explanation: uniqueProse(proseParts),
  };
}
